/**
 * PRD-F6 §TR / MAJOR-6 — Selection-format helpers for the TextBox slice.
 * Migrated verbatim from `objects/textbox-overlay.ts` (T7) so the slice
 * owns the public API surface without reaching into legacy modules.
 */

/**
 * All CSS properties that `applyTextFormatToSelection` can apply directly
 * to the selected range. `verticalScript` is not a CSS property — it is an
 * internal shorthand that combines `vertical-align: super | sub` with a
 * smaller font size.
 */
export type SelectionFormatKey =
  | 'color'
  | 'fontSize'
  | 'fontWeight'
  | 'fontStyle'
  | 'textDecoration'
  | 'textTransform'
  | 'verticalScript'
  // PRD-RTE-TIPTAP §TR7: toggle marks routed to the TipTap editor chain by
  // `TextboxEditorController.applyFormat` when an editor is open. They have
  // no DOM-level fallback in `applyTextFormatToSelection` (the legacy
  // contentEditable path is being retired).
  | 'bold'
  | 'italic'
  | 'strike'
  | 'underline';

/**
 * Wrap the current selection in a `<span>` carrying `styleProp: value`.
 * Uses `surroundContents` for the clean path and falls back to
 * extract + insert for ranges that cross element boundaries.
 *
 * @deprecated PRD-RTE-TIPTAP §T5.3 — This raw-DOM mark application path is
 * being retired by the TipTap migration. When a TipTap editor is open,
 * `TextboxEditorController.applyFormat` routes mark toggles
 * (`bold`/`italic`/`strike`/`underline`/`color`/`fontSize`) through the
 * editor chain instead. This function remains as a fallback for the
 * legacy keys (`fontWeight`/`fontStyle`/`textDecoration`/`textTransform`/
 * `verticalScript`) that have not yet been wired to TipTap marks — see
 * PRD-RTE-TIPTAP OPEN QUESTIONS for the deferred wrapper-style approach.
 * Outside-scope consumers (sticky-editor slice, etc.) may still call this
 * directly; do not delete without auditing all call sites.
 *
 * @returns `true` if the selection was modified, `false` if there was
 *          nothing to format (no selection / collapsed range).
 */
export function applyTextFormatToSelection(
  styleProp: SelectionFormatKey,
  value: string | number,
): boolean {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return false;
  const range = sel.getRangeAt(0);
  if (range.collapsed) return false;

  const span = document.createElement('span');
  if (styleProp === 'color') {
    span.style.color = String(value);
  } else if (styleProp === 'fontSize') {
    // Value can be a complete CSS expression (e.g. '0.44em') or a numeric
    // pixel fallback.
    span.style.fontSize = typeof value === 'string' ? value : `${value}px`;
  } else if (styleProp === 'fontWeight') {
    span.style.fontWeight = String(value);
  } else if (styleProp === 'fontStyle') {
    span.style.fontStyle = String(value);
  } else if (styleProp === 'textDecoration') {
    span.style.textDecoration = String(value);
  } else if (styleProp === 'textTransform') {
    span.style.textTransform = String(value);
  } else if (styleProp === 'verticalScript') {
    span.style.fontSize = '0.72em';
    span.style.verticalAlign = value === 'super' ? 'super' : 'sub';
  }

  try {
    range.surroundContents(span);
  } catch {
    const contents = range.extractContents();
    span.appendChild(contents);
    range.insertNode(span);
    span.parentElement?.normalize();
  }

  // Restore the selection over the same content (now inside the new span)
  // so consecutive format clicks keep working.
  const newRange = document.createRange();
  newRange.selectNodeContents(span);
  sel.removeAllRanges();
  sel.addRange(newRange);
  return true;
}

/**
 * Apply a font size (in world units) to the current selection.
 *
 * Compared to `applyTextFormatToSelection('fontSize', ...)`:
 *  - Processes each text node individually so multi-line selections do
 *    not get block-in-inline issues.
 *  - Stores the world-unit value in `data-font-wu` and writes absolute
 *    `px` (no `em` chains) so repeated application does not compound.
 *  - Reuses an existing `data-font-wu` span when the selection is already
 *    wrapped, avoiding span nesting.
 *
 * @param wu     Target size in world units (e.g. 18 for tier 4).
 * @param scale  Current viewport zoom (world units → screen pixels).
 * @returns      `true` if at least one text node was wrapped.
 */
export function applyFontSizeWuToSelection(wu: number, scale: number): boolean {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return false;
  const range = sel.getRangeAt(0);
  if (range.collapsed) return false;

  const items = collectTextNodesInRange(range);
  if (items.length === 0) return false;

  const pxValue = `${wu * scale}px`;
  let firstEl: Element | null = null;
  let lastEl: Element | null = null;

  for (const { node, start, end } of items) {
    // Isolate the selected slice of the text node. Order matters: split
    // from the back first, otherwise the front split shifts the indices.
    let target: Text = node;
    if (end < node.length) node.splitText(end);
    if (start > 0) target = node.splitText(start);

    // If the direct parent is already a font-wu span, just update it
    // (prevents nesting and the resulting compounding effect).
    const parent = target.parentElement;
    if (parent && parent.tagName === 'SPAN' && 'fontWu' in parent.dataset) {
      parent.dataset['fontWu'] = String(wu);
      parent.style.fontSize = pxValue;
      if (!firstEl) firstEl = parent;
      lastEl = parent;
      continue;
    }

    const span = document.createElement('span');
    span.dataset['fontWu'] = String(wu);
    span.style.fontSize = pxValue;
    target.parentNode!.insertBefore(span, target);
    span.appendChild(target);

    if (!firstEl) firstEl = span;
    lastEl = span;
  }

  if (firstEl && lastEl) {
    const newRange = document.createRange();
    newRange.setStartBefore(firstEl);
    newRange.setEndAfter(lastEl);
    sel.removeAllRanges();
    sel.addRange(newRange);
  }

  return firstEl !== null;
}

/**
 * Collect every text node inside `range` together with its relative
 * start / end offsets.
 */
function collectTextNodesInRange(
  range: Range,
): Array<{ node: Text; start: number; end: number }> {
  const root = range.commonAncestorContainer;
  const container =
    root.nodeType === Node.ELEMENT_NODE
      ? (root as Element)
      : root.parentElement!;

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const result: Array<{ node: Text; start: number; end: number }> = [];

  let node = walker.nextNode() as Text | null;
  while (node !== null) {
    if (range.intersectsNode(node)) {
      const start = node === range.startContainer ? range.startOffset : 0;
      const end = node === range.endContainer ? range.endOffset : node.length;
      if (start < end) result.push({ node, start, end });
    }
    node = walker.nextNode() as Text | null;
  }
  return result;
}
